/** GET /api/slurry/summary — one entry per WinCC variable logged by the slurry PC. */
export interface SlurrySummaryResponse {
  variables: Array<{
    varName: string;
    sourceFile: string;
    count: number;
    firstTs: string;
    lastTs: string;
    min: number;
    max: number;
    avg: number;
  }>;
}

/**
 * Slurry channels shown in the portal, in display order. Where the slurry
 * PC logs a value both over Modbus (RP850_Data_*) and as a scaled 4-20 mA
 * input (*_ScaledValue), the Modbus value is used; Viscosity_ScaledValue
 * and Density_ScaledValue stay in the table but aren't shown.
 */
export const SLURRY_CHANNELS = {
  branchFlow: {
    varName: "Flowmeter_ScaledValue",
    label: "Branch Flowrate",
    unit: "m³/h",
    description: "Flowrate of slurry across sensing piece through bypass branch.",
  },
  liveViscosity: {
    varName: "RP850_Data_Live Viscosity",
    label: "Live Viscosity",
    unit: "cP",
    description: "Current measured plastic viscosity of slurry.",
  },
  averagedViscosity: {
    varName: "RP850_Data_Average Corrected Viscosity",
    label: "Averaged Viscosity",
    unit: "cP",
    description:
      "Time-averaged measured plastic viscosity of slurry. Averaging set on the RP850 data processor.",
  },
  yieldPoint: {
    varName: "RP850_Data_Yp",
    label: "Yield Point",
    unit: "Pa",
    description: "Yield point of slurry.",
  },
  density: {
    varName: "RP850_Data_Density",
    label: "Density",
    unit: "g/cm³",
    description: "Slurry mudweight.",
  },
  temperature: {
    varName: "RP850_Data_Temperature",
    label: "Temperature",
    unit: "°C",
    description: "Slurry temperature.",
  },
  flowPid: {
    varName: "Flow Control PID_PIDOutput",
    label: "Flow PID",
    unit: "%",
    description: "Modulating flow control.",
  },
  valvePosition: {
    varName: "Modulating Valve_ValvePositionFeedback",
    label: "Valve Position",
    unit: "%",
    description: "Valve position of actuated ball valve controlling modulating flow.",
  },
} as const;

export type SlurryChannelId = keyof typeof SLURRY_CHANNELS;

/** GET /api/slurry/series */
export interface SlurrySeriesResponse {
  /** Bucket width in seconds when the window was averaged down; null = raw. */
  bucketSeconds: number | null;
  series: Array<{
    channel: SlurryChannelId;
    points: Array<{ t: string; v: number }>;
  }>;
}

/**
 * Manual slurry readings entered on /slurry (lab / mud-balance / Marsh
 * funnel checks). Plotted as markers over the matching slurry channel's
 * axis — `channels` lists the chips each kind overlays, first visible
 * one wins.
 */
export const SLURRY_MANUAL_KINDS = {
  plasticViscosity: {
    label: "Plastic Viscosity",
    unit: "cP",
    channels: ["liveViscosity", "averagedViscosity"],
  },
  yieldPoint: { label: "Yield Point", unit: "Pa", channels: ["yieldPoint"] },
  density: { label: "Density", unit: "g/cm³", channels: ["density"] },
  temperature: { label: "Temperature", unit: "°C", channels: ["temperature"] },
  marshFunnel: {
    // Value entered is funnel time (s); plotted as apparent viscosity.
    label: "Marsh Funnel",
    unit: "s",
    channels: ["liveViscosity", "averagedViscosity"],
  },
} as const satisfies Record<
  string,
  { label: string; unit: string; channels: readonly SlurryChannelId[] }
>;

export type SlurryManualKind = keyof typeof SLURRY_MANUAL_KINDS;

/**
 * Apparent viscosity from a Marsh funnel time, Pitt (2000), "The Marsh
 * Funnel and Drilling Fluid Viscosity: A New Equation for Field Use",
 * SPE Drilling & Completion 15(1): μa = ρ (t − 25), μa in cP, ρ in
 * g/cm³, t = time in s for one quart (946 mL) to drain. Empirical;
 * null when the time is at or below the 25 s water offset.
 */
export function marshApparentViscosity(timeS: number, densityGcm3: number): number | null {
  if (!(timeS > 25) || !(densityGcm3 > 0)) return null;
  return densityGcm3 * (timeS - 25);
}

export interface SlurryManualReading {
  id: number;
  kind: SlurryManualKind;
  ts: string;
  /** As entered (Marsh funnel: seconds). */
  value: number;
  /** Marsh funnel only: density used for the conversion. */
  density: number | null;
  /** Marsh funnel only: apparent viscosity in cP (what gets plotted). */
  apparentViscosity: number | null;
  note: string;
}

export interface SlurryManualCreateBody {
  kind: SlurryManualKind;
  ts: string;
  value: number;
  density?: number;
  note?: string;
}
